import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/SubscriptionItemsService';
import { useState } from 'react';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useSubscriptionItems = (subscriptionId: string) => {
    if (!subscriptionId) {
        throw new Error('subscriptionId is required parameter');
    }

    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});
    
    const sort = { column: 'created', direction: 'desc' as const };
    
    // INFINITE QUERY
    const subscriptionItemsQuery = useInfiniteQuery({
        queryKey: ['subscription-items', subscriptionId, keyword, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords(subscriptionId, { limit, sort, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Subscription Item deleted');
            queryClient.invalidateQueries({ queryKey: ['subscription-items', subscriptionId] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({ queryKey: ['subscription-items'] });
    };

    const loadMoreRecords = () => {
        if (subscriptionItemsQuery.hasNextPage) {
            subscriptionItemsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const handleClearRecords = () => {
        queryClient.removeQueries({ queryKey: ['subscription-items', subscriptionId] });
    };

    const allItems = subscriptionItemsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: subscriptionItemsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: subscriptionItemsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        loadMoreRecords,
        deleteRecords,
        handleFilterByKeyword,
        handleClearRecords,
        deleteMutation
    };
};